import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections, initialValues } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IProviderDrawerInfo, IProviderDrawerProps } from '.';
import {
  createProvider,
  updateProvider,
  fetchedPersonsWithPagination,
  getProvidersLovs,
} from './queries';
import { extractPersons, toLookups } from './utils';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import DateService from '@services/DateService';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IAbstractRecord, IPagination } from '@models';
import { isGuid } from '@utils/validationUtils';

const useStyles = makeStyles()(() => ({
  isLocalStyle: {
    marginBottom: '17px',
  },
}));

const ProviderDrawer: React.FC<IProviderDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  providerDetailsInfo,
  providerId,
}) => {
  const { classes } = useStyles();
  classes;
  const navigate = useNavigate();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [booted, setBooted] = useState<boolean>(false);

  const [values, setValues] = useState<IProviderDrawerInfo>(initialValues);
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(values, lovs, providerId)
  );

  const [providersDetailsListResults] = useLazyQuery(getProvidersLovs);

  const [getPersonsListLazy] = useLazyQuery(fetchedPersonsWithPagination);

  const [providerAction] = useMutation(
    providerId ? updateProvider() : createProvider()
  );

  const getLovs = async () => {
    const data = await providersDetailsListResults();
    const lookups = toLookups(data.data);
    const result: Record<string, Record<string, string>> = {
      providerStatus: lookups['providerStatus'],
      type: lookups['type'],
    };

    return result;
  };

  const initialize = async () => {
    try {
      const newLovs = await getLovs();
      const newValues = cloneDeep(providerDetailsInfo);

      setLovs(newLovs);
      setValues(providerDetailsInfo);
      setInputsForm(drawerSections(newValues, newLovs, providerId));
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: IProviderDrawerInfo) => {
    const [data] = normalizeDynamicFormDrawerValues(
      drawerSections(values, lovs, providerId),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      let variablesMutation = {};

      if (!providerId) {
        variablesMutation = {
          personId: data.person,
          type: data.type,
          isCompany: data.isCompany ? data.isCompany : false,
          isLocal: data.isLocal ? data.isLocal : false,
          effectiveDate: DateService.formatDateBackend(data.effectiveDate),
          expiryDate: DateService.formatDateBackend(data.expiryDate),
          status: data.providerStatus,
        };
      } else {
        variablesMutation = {
          isCompany: data.isCompany ? data.isCompany : false,
          isLocal: data.isLocal ? data.isLocal : false,
          effectiveDate: DateService.formatDateBackend(data.effectiveDate),
        };
      }

      const res = await providerAction({
        variables: providerId
          ? {
              ...variablesMutation,
              entityId: providerId,
            }
          : variablesMutation,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {providerId
              ? 'Provider successfully updated.'
              : 'Provider successfully created.'}
          </ToastSuccessMessage>
        );

        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
          setFormDisabled(false);
          if (!providerId) {
            const newProviderId =
              res.data.configuration.actions.createProvider.id;
            navigate(`/configuration/providers/` + newProviderId);
          }
        }, 500);
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const onCustomValidate = (fieldName: string, values: Record<string, any>) => {
    if (fieldName === 'effectiveDate' || fieldName === 'expiryDate') {
      const { effectiveDate, expiryDate } = values;
      if (
        expiryDate &&
        effectiveDate &&
        DateService.isDateBefore(effectiveDate, expiryDate)
      ) {
        return 'Expiry Date shall be equal or greater than Effective Date';
      }
      return '';
    }

    return '';
  };

  const handleChange = (
    fieldName: keyof IProviderDrawerInfo,
    value: any,
    sectionIndex: number,
    allValues: IProviderDrawerInfo,
    errors: IAbstractRecord,
    touched: IAbstractRecord
  ) => {
    const newSections = cloneDeep(inputsForm);
    newSections[sectionIndex].inputs[fieldName].value = value;
    setValues(allValues);
    setInputsForm(newSections);
    if (fieldName === 'effectiveDate' || fieldName === 'expiryDate') {
      const expiryDateError = onCustomValidate('expiryDate', allValues);
      errors.expiryDate = expiryDateError;
      touched.effectiveDate = true;
      touched.expiryDate = true;
    }
  };

  return (
    <DynamicFormDrawer
      title={providerId ? 'Modify Provider Details' : 'New Provider'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      submitButtonState={submitButtonState}
      onSubmit={(values) => submitForm(values as IProviderDrawerInfo)}
      disableForm={formDisabled}
      onChange={handleChange}
      onAutocompleteSearch={(
        fieldName: string,
        value: string,
        pagination: IPagination
      ) => {
        if (fieldName === 'person') {
          const variables = isGuid(value)
            ? {
                selectedPersonID: value,
                pagination,
              }
            : {
                searchKeyword: value,
                pagination,
              };
          return getPersonsListLazy({
            variables: variables,
          }).then((result) => {
            const personsList = extractPersons(
              result.data.SalesforceManagement.queries.getPersonList.items
            );

            return {
              options: personsList,
              totalCount:
                result.data?.SalesforceManagement?.queries?.getPersonList
                  ?.paging?.totalCount,
            };
          });
        }

        return new Promise<{
          options: Record<string, string>;
          totalCount: number;
        }>((resolve) => {
          resolve({ options: {}, totalCount: 0 });
        });
      }}
    />
  );
};

export default ProviderDrawer;
